<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { dispatchSchedulesApi } from '@/api/dispatchSchedules.js'
import { createLatestReadGate } from '@/utils/latestReadGate.js'
import { phaseLabel } from '@/utils/dispatchScheduleForm.js'

const props = defineProps({ tenantId: String, project: Object })
const emit = defineEmits(['batch'])
const filters = ref({ from_date: '', to_date: '', kind: '', schedule_id: '' })
const result = ref({ items: [], total: 0, offset: 0 })
const rules = ref([])
const busy = ref(false)
const stopping = ref('')
const error = ref('')
const reads = createLatestReadGate()
const initial = [props.tenantId, props.project.id]
let disposed = false
const current = () => !disposed && props.tenantId === initial[0] && props.project.id === initial[1]
async function load(offset = 0) {
  if (!current()) return
  if (filters.value.from_date && filters.value.to_date && filters.value.from_date > filters.value.to_date) { error.value = '开始日期不能晚于结束日期。'; return }
  const ticket = reads.begin(JSON.stringify(filters.value))
  busy.value = true
  try {
    const response = await dispatchSchedulesApi.occurrences(initial[0], initial[1], { ...filters.value, offset, limit: 20 })
    if (current() && reads.accepts(ticket, JSON.stringify(filters.value))) { result.value = response; error.value = '' }
  } catch (failure) { if (current() && reads.accepts(ticket, JSON.stringify(filters.value))) error.value = failure?.message || '执行记录暂时无法读取。' }
  finally { if (current() && reads.accepts(ticket, JSON.stringify(filters.value))) busy.value = false }
}
async function stop(entry) {
  if (stopping.value || !current()) return
  if (!window.confirm('停止这一次尚未完成的执行，保留已有内容、图片和其他日程。确认停止吗？')) return
  stopping.value = entry.id
  try {
    await dispatchSchedulesApi.stopOccurrence(initial[0], initial[1], entry.id, {
      expected_attempt: entry.attempt, idempotency_key: `stop-occurrence-v1-${entry.id}`,
    })
    if (current()) await load(result.value.offset)
  } catch (failure) { if (current()) error.value = failure?.message || '停止结果尚未确认，请重新读取本次执行。' }
  finally { stopping.value = '' }
}
function when(entry) {
  return new Intl.DateTimeFormat('zh-CN', { timeZone: entry.timezone, dateStyle: 'short', timeStyle: 'short' }).format(new Date(entry.planned_at))
}
onMounted(async () => {
  await load()
  try { const response = await dispatchSchedulesApi.list(initial[0], initial[1]); if (current()) rules.value = response.items || [] } catch { /* Date and kind searches remain available. */ }
})
onBeforeUnmount(() => { disposed = true; reads.invalidate() })
</script>

<template>
  <section class="occurrence-history" aria-label="日程执行记录">
    <h2>日程执行记录</h2>
    <div class="filters">
      <label>开始日期<input v-model="filters.from_date" type="date" @change="load(0)"></label>
      <label>结束日期<input v-model="filters.to_date" type="date" @change="load(0)"></label>
      <label>下发方式<select v-model="filters.kind" @change="load(0)"><option value="">全部日程</option><option value="periodic">周期自动下发</option><option value="once">预设下发</option></select></label>
      <label>日程<select v-model="filters.schedule_id" @change="load(0)"><option value="">全部规则</option><option v-for="rule in rules" :key="rule.id" :value="rule.id">{{ rule.name || (rule.kind === 'once' ? rule.once_local : rule.local_time) }}</option></select></label>
      <button :disabled="busy" @click="load(result.offset)">重新读取执行记录</button>
    </div>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <p v-if="busy" role="status">正在读取执行记录…</p>
    <p v-if="!busy && !result.items.length">所选范围没有执行记录。</p>
    <article v-for="entry in result.items" :key="entry.id">
      <header><h3>{{ entry.name || (entry.kind === 'once' ? '预设下发' : '周期自动下发') }}</h3><strong>{{ phaseLabel(entry.phase) }}</strong></header>
      <p>计划下发：{{ when(entry) }} · {{ entry.timezone }}</p>
      <p v-if="entry.selected_count">内容就绪 {{ entry.ready_count }}/{{ entry.selected_count }} · 手机接收 {{ entry.received_count }}/{{ entry.selected_count }} · 图片生成 {{ entry.generated_count }}/{{ entry.selected_count }}</p>
      <p v-else>已保存本次执行，等待必要资料与接收手机。</p>
      <p v-if="entry.error_message" class="error">{{ entry.error_message }}</p>
      <details v-if="entry.materials?.length"><summary>本批使用的两张素材与说明</summary><div v-for="(material, index) in entry.materials" :key="material.material_id"><b>图片 {{ index + 1 }}：{{ material.name }}</b><p class="description">{{ material.description }}</p></div></details>
      <footer><button v-if="entry.dispatch_batch_id" @click="emit('batch', entry)">查看手机任务</button>
        <button v-if="entry.phase !== 'stopped'" :disabled="Boolean(stopping)" @click="stop(entry)">{{ stopping === entry.id ? '正在停止…' : '停止本次执行' }}</button>
      </footer>
    </article>
    <footer><button :disabled="busy || result.offset === 0" @click="load(Math.max(0, result.offset - 20))">上一页</button><span>共 {{ result.total }} 次执行</span><button :disabled="busy || result.offset + result.items.length >= result.total" @click="load(result.offset + 20)">下一页</button></footer>
  </section>
</template>

<style scoped>
.occurrence-history { padding: 24px; border: 1px solid #d3e4e6; border-radius: 18px; background: #fff; }
h2, h3 { margin: 0; color: #173a44; }
.filters { display: flex; flex-wrap: wrap; gap: 12px; margin: 16px 0; }
label { display: grid; gap: 7px; color: #617f88; }
input, select { min-height: 36px; max-width: 100%; border: 1px solid #c8dcdf; border-radius: 8px; padding: 6px; font: inherit; }
article { margin: 14px 0; padding: 16px; border: 1px solid #d3e4e6; border-radius: 12px; }
header, footer { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; justify-content: space-between; }
p { color: #617f88; line-height: 1.6; overflow-wrap: anywhere; }
strong, summary { color: #08746d; }
button { padding: 8px 12px; border: 1px solid #8bbeba; border-radius: 9px; color: #08746d; background: #eff9f8; font: inherit; }
button:disabled { opacity: .55; }
.description { white-space: pre-wrap; }
.error { color: #a3342c; }
@media(max-width:700px) { .occurrence-history { padding: 16px; } .filters > label { width: 100%; } }
</style>
